import * as React from 'react';
import throttle from 'lodash/throttle';
import ResizeObserver from 'resize-observer-polyfill';

const SCROLL_TOLERANCE = 1;

const useShowBottomFade = (): {
  scrollRef: React.MutableRefObject<HTMLDivElement | null>;
  contentRef: React.MutableRefObject<HTMLDivElement | null>;
  showFade: boolean;
  handleScroll: () => void;
  recheckFade: () => void;
} => {
  const [showFade, setShowFade] = React.useState<boolean>(false);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const contentRef = React.useRef<HTMLDivElement | null>(null);

  const recheckFade = React.useCallback(() => {
    const element = scrollRef.current;

    if (!element) {
      return;
    }

    const hasScroll = element.scrollHeight > element.clientHeight;
    const atBottom =
      element.scrollTop + element.clientHeight >=
      element.scrollHeight - SCROLL_TOLERANCE;

    setShowFade(hasScroll && !atBottom);
  }, []);

  const handleScroll = React.useMemo(
    () => throttle(recheckFade, 100),
    [recheckFade]
  );

  React.useEffect(() => {
    const scrollEl = scrollRef.current;
    const contentEl = contentRef.current;

    if (!scrollEl) {
      return;
    }

    recheckFade();

    const observer = new ResizeObserver(() => {
      recheckFade();
    });

    observer.observe(scrollEl);

    if (contentEl) {
      observer.observe(contentEl);
    }

    return () => {
      observer.disconnect();
    };
  }, [recheckFade]);

  return {
    scrollRef,
    contentRef,
    showFade,
    handleScroll,
    recheckFade
  };
};

export default useShowBottomFade;
